import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TenantSettingsStoreService } from '../services/tenant-settings-store.service';
import { ITenantSettings, IUpdateTenantSettingsRequest } from '../models/tenant-setting';
import { Observable } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { masketTextBoxValidator } from 'src/app/shared/validators/masketTextBox.validator';
import { flexibleTimeValidator, timeStringRangeValidator } from 'src/app/shared/validators/timeStringValidator.validator';
import { EmployeeService } from 'src/app/employee/services/employee.service';
import { AppNotificationService } from 'src/app/shared/services/notification.service';
import { translate } from '@jsverse/transloco';
import { TimeSpan } from 'src/app/shared/helpers/time-span';

@UntilDestroy()
@Component({
    selector: 'app-tenant-settings',
    templateUrl: './tenant-settings.component.html',
    styleUrls: ['./tenant-settings.component.css'],
    standalone: false
})
export class TenantSettingsComponent implements OnInit {

  constructor(
    private tenantSettingsStore: TenantSettingsStoreService,
    private employeeService : EmployeeService,
    private appnotificationService: AppNotificationService
  ) { }

  editMask = "AB:CD";
  maskRules: { [key: string]: RegExp } = {
    A: /[0-2]+/,
    B: /[0-9]+/,
    C: /[0-5]+/,
    D: /[0-9]+/,
  };

  tenantSettings$ : Observable<ITenantSettings> =
    this.tenantSettingsStore.tenantSettings$;

  form: UntypedFormGroup = new UntypedFormGroup({
    id: new UntypedFormControl(null),
    startMorningWorkTime: new UntypedFormControl(null, [Validators.required,masketTextBoxValidator("  :  ")]),
    endMorningWorkTime: new UntypedFormControl(null, [Validators.required,masketTextBoxValidator("  :  ")]),
    startAfternoonWorkTime: new UntypedFormControl(null, [Validators.required,masketTextBoxValidator("  :  ")]),
    endAfternoonWorkTime: new UntypedFormControl(null, [Validators.required,masketTextBoxValidator("  :  ")]),
    defaultWorkTime: new UntypedFormControl(null, [Validators.required,masketTextBoxValidator("  :  ")]),
    preliminaryWorktime: new UntypedFormControl(null, [Validators.required,masketTextBoxValidator("  :  ")]),
    preWorkedHolidayLenght: new UntypedFormControl(null, [Validators.required,masketTextBoxValidator("  :  ")]),
    holidayColor: new UntypedFormControl(null, Validators.required),
    timeEntryModule: new UntypedFormControl(null),
    schedulerModule: new UntypedFormControl(null),
    assignmentModule: new UntypedFormControl(null),
    vacationModule: new UntypedFormControl(null),
    contactModule: new UntypedFormControl(null),
    expenseModule: new UntypedFormControl(null),
    hasTimeSupplements: new UntypedFormControl(false),
    weeklyMaxWorkTime: new UntypedFormControl("00:00", [masketTextBoxValidator("  :  ")]),
    weeklyTimeSupplementPercentage: new UntypedFormControl(0, [Validators.min(0), Validators.max(100)]),
    hasOvertime: new UntypedFormControl(false),
    overtimeWeeklyMaxWorkTime: new UntypedFormControl(
      { value: "45:00", disabled: true },
      [Validators.required, flexibleTimeValidator(), timeStringRangeValidator(168)]
    ),
    lunchBreakControlEnabled: new UntypedFormControl(false),
    lunchWindowStart: new UntypedFormControl({ value: "11:00", disabled: true }, [Validators.required, masketTextBoxValidator("  :  ")]),
    lunchWindowEnd: new UntypedFormControl({ value: "14:00", disabled: true }, [Validators.required, masketTextBoxValidator("  :  ")]),
    lunchBreakMinDuration: new UntypedFormControl({ value: "00:30", disabled: true }, [Validators.required, masketTextBoxValidator("  :  ")]),
    openTimeEntryControlEnabled: new UntypedFormControl(false),
  })

  ngOnInit(): void {
    this.initFormValues();
  }

  // The weekly overtime limit is only editable - and only mandatory - while overtime is switched on.
  onHasOvertimeChange(hasOvertime: boolean): void {
    const control = this.form.get('overtimeWeeklyMaxWorkTime');

    if(hasOvertime){
      control.enable();
    } else {
      control.disable();
    }
  }

  // The lunch window and the required break are only editable while the control is switched on;
  // the backend ignores them otherwise.
  onLunchBreakControlChange(enabled: boolean): void {
    ['lunchWindowStart', 'lunchWindowEnd', 'lunchBreakMinDuration'].forEach(name => {
      const control = this.form.get(name);
      if (enabled) {
        control.enable();
      } else {
        control.disable();
      }
    });
  }

  initFormValues(){
    this.tenantSettings$
    .pipe(untilDestroyed(this))
    .subscribe(tenantSettings => {
      if(tenantSettings){
        this.form.patchValue({
          id: tenantSettings.id,
          startMorningWorkTime: tenantSettings.startMorningWorkTime,
          endMorningWorkTime: tenantSettings.endMorningWorkTime,
          startAfternoonWorkTime: tenantSettings.startAfternoonWorkTime,
          endAfternoonWorkTime: tenantSettings.endAfternoonWorkTime,
          defaultWorkTime: tenantSettings.defaultWorkTime,
          preliminaryWorktime: tenantSettings.preliminaryWorktime,
          preWorkedHolidayLenght: tenantSettings.preWorkedHolidayLenght,
          holidayColor: tenantSettings.holidayColor,
          timeEntryModule: tenantSettings.timeEntryModule,
          schedulerModule: tenantSettings.schedulerModule,
          assignmentModule: tenantSettings.assignmentModule,
          vacationModule: tenantSettings.vacationModule,
          contactModule: tenantSettings.contactModule,
          expenseModule: tenantSettings.expenseModule,
          hasTimeSupplements: tenantSettings.hasTimeSupplements,
          weeklyMaxWorkTime: tenantSettings.weeklyMaxWorkTimeObj?.toStringShort(),
          weeklyTimeSupplementPercentage: tenantSettings.weeklyTimeSupplementPercentage,
          hasOvertime: tenantSettings.hasOvertime,
          overtimeWeeklyMaxWorkTime: tenantSettings.overtimeWeeklyMaxWorkTimeObj?.toStringShort(),
          lunchBreakControlEnabled: tenantSettings.lunchBreakControlEnabled,
          lunchWindowStart: tenantSettings.lunchWindowStart,
          lunchWindowEnd: tenantSettings.lunchWindowEnd,
          lunchBreakMinDuration: tenantSettings.lunchBreakMinDuration,
          openTimeEntryControlEnabled: tenantSettings.openTimeEntryControlEnabled ?? false
        })

        this.onHasOvertimeChange(tenantSettings.hasOvertime);
        this.onLunchBreakControlChange(tenantSettings.lunchBreakControlEnabled);
      }
    })
  }

  onSubmit(){
    // getRawValue(), because the disabled overtime limit still has to be sent back unchanged.
    const formValues = this.form.getRawValue();

    let request : IUpdateTenantSettingsRequest = {
      startMorningWorkTime: TimeSpan.parse(formValues.startMorningWorkTime).toString(),
      endMorningWorkTime:TimeSpan.parse(formValues.endMorningWorkTime).toString(),
      startAfternoonWorkTime:TimeSpan.parse(formValues.startAfternoonWorkTime).toString(),
      endAfternoonWorkTime:TimeSpan.parse(formValues.endAfternoonWorkTime).toString(),
      defaultWorkTime:TimeSpan.parse(formValues.defaultWorkTime).toString(),
      preWorkedHolidayLenght:TimeSpan.parse(formValues.preWorkedHolidayLenght).toString(),
      preliminaryWorktime:TimeSpan.parse(formValues.preliminaryWorktime).toString(),
      holidayColor: formValues.holidayColor,
      hasTimeSupplements: formValues.hasTimeSupplements,
      weeklyMaxWorkTime: TimeSpan.parse(formValues.weeklyMaxWorkTime).toString(),
      weeklyTimeSupplementPercentage: formValues.weeklyTimeSupplementPercentage,
      hasOvertime: formValues.hasOvertime,
      overtimeWeeklyMaxWorkTime: TimeSpan.parse(formValues.overtimeWeeklyMaxWorkTime).toString(),
      lunchBreakControlEnabled: formValues.lunchBreakControlEnabled,
      lunchWindowStart: TimeSpan.parse(formValues.lunchWindowStart).toString(),
      lunchWindowEnd: TimeSpan.parse(formValues.lunchWindowEnd).toString(),
      lunchBreakMinDuration: TimeSpan.parse(formValues.lunchBreakMinDuration).toString(),
      openTimeEntryControlEnabled: formValues.openTimeEntryControlEnabled ?? false
    }

    this.tenantSettingsStore.updateCurrentTenantSettings(request);
  }

  
  onRecalculate(){
    this.employeeService.recalculateEmployees().subscribe(
      response => {
        this.appnotificationService.notification$.next(
          {message: translate('employees.recalculationSuccess'), style: 'success'}
          )
      }
    );
  }

}
